import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { Quiz, QuizTag } from '@superfunnel/types';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Button from '@superfunnel/ui-core/v2/Button';
import { Popover, PopoverTrigger } from '@superfunnel/ui-core/v2/popover';
import {
  PopoverPanelBody,
  PopoverPanelContent,
  PopoverPanelHeader,
  PopoverPanelTitle,
} from '@superfunnel/ui-core/v2/PopoverPanel';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/v2/Tabs';

interface QuizFilters {
  tags: QuizTag[];
  statuses: Quiz['status'][];
  changes: boolean[];
}

export type PartialQuizFilters = {
  tags?: string[];
  statuses?: QuizFilters['statuses'];
  changes?: boolean[];
};

interface Props {
  children: ReactNode;
  disabled?: boolean;
  filters: PartialQuizFilters;
  filterOptions: QuizFilters;
  onFiltersChange: (filters: PartialQuizFilters) => void;
  onReset: () => void;
}

export default function QuizFunnelsFilterPopover({
  children,
  disabled,
  filters,
  filterOptions,
  onFiltersChange,
  onReset,
}: Props) {
  const [open, setOpen] = useState(false);

  // Initialize selectedQuizTags from current filters
  const selectedQuizTags = useMemo(() => {
    if (!filters.tags || !filterOptions.tags) return [];
    return filterOptions.tags.filter((tag) => filters.tags?.includes(tag.id));
  }, [filters.tags, filterOptions.tags]);

  const handleTagsChange = (newTags: QuizTag[]) => {
    onFiltersChange({
      ...filters,
      tags: newTags.map((tag) => tag.id),
    });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      (filters.statuses && filters.statuses.length > 0) ||
      (filters.changes && filters.changes.length > 0) ||
      (filters.tags && filters.tags.length > 0)
    );
  }, [filters]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverPanelContent width="md" align="end">
        <PopoverPanelHeader>
          <div className="flex w-full items-center justify-between gap-2">
            <PopoverPanelTitle>Filters</PopoverPanelTitle>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="micro"
                onClick={onReset}
                className="text-sm"
              >
                Reset all
              </Button>
            )}
          </div>
        </PopoverPanelHeader>
        <PopoverPanelBody className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <h4 className="text-base-700 text-sm font-medium">Status</h4>
            <Tabs
              value={
                filters.statuses?.length === 0 || !filters.statuses
                  ? 'all'
                  : filters.statuses?.length === filterOptions.statuses.length
                    ? 'all'
                    : filters.statuses?.[0] || ''
              }
              onValueChange={(value) => {
                if (value === 'all') {
                  onFiltersChange({
                    ...filters,
                    statuses: [],
                  });
                } else {
                  onFiltersChange({
                    ...filters,
                    statuses: [value] as QuizFilters['statuses'],
                  });
                }
              }}
            >
              <TabsList className="w-full">
                <TabsTrigger value="all" className="flex-1">
                  All
                </TabsTrigger>
                {filterOptions.statuses.map((status) => (
                  <TabsTrigger key={status} value={status} className="flex-1">
                    {status === 'active' ? 'Active' : 'Inactive'}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Changes */}
          <div className="space-y-1.5">
            <h4 className="text-base-700 text-sm font-medium">Changes</h4>
            <Tabs
              value={
                filters.changes?.length === 0 || !filters.changes
                  ? 'all'
                  : filters.changes?.length === filterOptions.changes.length
                    ? 'all'
                    : String(filters.changes?.[0]) || ''
              }
              onValueChange={(value) => {
                if (value === 'all') {
                  onFiltersChange({
                    ...filters,
                    changes: [],
                  });
                } else {
                  onFiltersChange({
                    ...filters,
                    changes: [value === 'true'],
                  });
                }
              }}
            >
              <TabsList className="w-full">
                <TabsTrigger value="all" className="flex-1">
                  All
                </TabsTrigger>
                {filterOptions.changes.map((change) => (
                  <TabsTrigger
                    key={String(change)}
                    value={String(change)}
                    className="flex-1"
                  >
                    {change ? 'Pending' : 'Deployed'}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Tags */}
          <div className="space-y-1.5">
            <ChipsAutocomplete<QuizTag>
              title="Tags"
              placeholder="Search tags..."
              options={filterOptions.tags || []}
              getOptionId={(c) => c.id}
              getValue={(c) => c.name}
              selected={selectedQuizTags}
              onChange={handleTagsChange}
              className="w-full"
            />
          </div>
        </PopoverPanelBody>
      </PopoverPanelContent>
    </Popover>
  );
}
